// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import * as React from 'react';
import Logging from '@ta/common.logging';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import { Opener } from '@ta/overlays.managers';
import { DeleteConfirmation, UntagConfirmation } from '@ta/social.actions';
import { FollowAction, FollowText, type GatedFollowAction } from '@ta/social.follow';
import { OverflowUI, OverflowUIList, OverflowUIListItem } from '@ta/social.overflow-ui';
import { InteractionLogger } from '@ta/tracking.interactions';
import type { InteractionType } from '@ta/tracking.interactions';
import { SocialLoginGate } from '@ta/social.login-gate';

import {
  ReportLinkModal,
  ReportReviewModal,
  ReportPhotoModal,
  ReportVideoModal,
  ReportForumModal,
  ReportTripModal,
  ReportRepostModal,
  ReportLocationTopicModal,
  ReportLocationTopicPostModal,
  reportPropsFromObject,
  ReportOwnerResponseModal,
} from '@ta/public.iap-reporting';

import { HideOnReadonly } from '@ta/failover.readonly';

import styles from './SectionOverflowMenu.less';

// The underlying object the section represents
/** A superset of the fields needed from the object of a section */
export type SectionObject = {
  /** The typename */
  __typename: string,
  /** Possible differing IDs for a node */
  id?: number,
  /** Possible differing IDs for a node */
  postId?: number,
  /** Possible differing IDs for a node */
  repostId?: number,
  /** Possible differing IDs for a node */
  // May correspond to a forum
  forumId?: number,
  /** May correspond to a topic */
  topicId?: number,
  /** May contain a location object */
  location?: any,
  /** May be a repost of another object */
  repostedObject?: SectionObject,
};

// The superset of the fields needed from the actor of a section
type SectionActor = {
  userId: string,
  displayName: string,
  isVerified: boolean,
  isFollowing: boolean,
  followerCount?: number,
  followeeCount?: number,
  isMe?: boolean,
};

/** The overflow item types */
export type SectionOverflowItemType = 'REPORT' | 'DELETE' | 'EDIT' | 'FOLLOW' | 'UNTAG';

/** Overrides for the overflow item */
export type OverflowItemOverride = { [SectionOverflowItemType]: boolean };

type ExternalProps = {|
  /** Object-specific info */

  /** The actor pertaining to the section */
  sectionActor: ?SectionActor,
  /** The object pertaining to the section */
  sectionObject?: SectionObject,
  /** A mapping of an object to map to a specific overflowType, for finer granularity */
  sectionObjectForType?: { [SectionOverflowItemType]: SectionObject },
  /** Force set an overflow item type, overriding the default business logic for display.
   * This will NOT override feature checks, and overflow items that doesn't have the necessary data will still not display */
  overrideItems?: OverflowItemOverride,
  /** If true, overflow menu will close when the user continues to scroll. */
  closeOverflowOnScroll?: boolean,
  /** Action-specific callbacks */
  deleteObject: ?() => void,
  onEdit?: () => mixed,
  untagObject?: ?() => void,
|};

type WithFollowActionProps = {|
  ...ExternalProps,
  gatedAction: GatedFollowAction,
|};

type Props = {|
  ...WithFollowActionProps,
  onReport: () => mixed,
  reportModalName: string,
  loginGate: (pid?: number) => Promise<*>,
  logInteraction: (
    event: SyntheticEvent<*>,
    interactionType: InteractionType,
    variables?: {},
  ) => mixed,
|};

const SUPPORTED_EDIT_TYPES = ['Video', 'Photo', 'LinkPost'];

const SUPPORTED_REPORT_TYPES = [
  'Review',
  'ReviewMgmtResponse',
  'Photo',
  'Video',
  'LinkPost',
  'ForumPost',
  'Trip',
  'RoomTip',
  'ApiLocationTopic',
  'ApiTopicPosting',
];

const fromEntries = entries => {
  const obj = {};
  entries.forEach(([key, value]) => {
    obj[key] = value;
  });
  return obj;
};

class SectionOverflowMenu extends React.Component<Props> {
  static defaultProps = {
    onEdit: undefined,
    untagObject: undefined,
    deleteObject: undefined,
    sectionObjectForType: undefined,
    closeOverflowOnScroll: false,
  };

  renderReportModal = (sectionObject: SectionObject) => {
    const { reportModalName } = this.props;
    const userId = this.props.sectionActor && this.props.sectionActor.userId;
    const reportInfo = reportPropsFromObject(sectionObject);

    switch (sectionObject.__typename) {
      case 'RoomTip':
      case 'Review':
        return <ReportReviewModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'ReviewMgmtResponse':
        return (
          <ReportOwnerResponseModal
            modalName={reportModalName}
            userId={userId}
            responseId={reportInfo.contentId}
          />
        );
      case 'Photo':
        return <ReportPhotoModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'Video':
        return <ReportVideoModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'LinkPost':
        return <ReportLinkModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'ForumPost':
        return <ReportForumModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'Trip':
        return <ReportTripModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'Repost':
        return <ReportRepostModal {...reportInfo} modalName={reportModalName} key="report" />;
      case 'ApiLocationTopic':
        return (
          <ReportLocationTopicModal {...reportInfo} modalName={reportModalName} key="report" />
        );
      case 'ApiTopicPosting':
        return (
          <ReportLocationTopicPostModal {...reportInfo} modalName={reportModalName} key="report" />
        );
      default:
        // This should never happen
        Logging.error(
          `Tried to report unsupported report type ${sectionObject.__typename}:${
            reportInfo.contentId
          }`,
        );
        return null;
    }
  };

  renderList = ({
    reportEnabled,
    editEnabled,
    deleteEnabled,
    untagEnabled,
    followEnabled,
    onDelete,
    onUntag,
    mergedSectionObjectForType,
  }) => {
    const {
      onEdit,
      onReport,
      loginGate,
      logInteraction,
      sectionActor,
      closeOverflowOnScroll,
    } = this.props;

    const overflowItems = [];

    if (reportEnabled) {
      overflowItems.push(
        <OverflowUIListItem
          key="report"
          onClick={({ event, close }) => {
            logInteraction(event, 'report');
            const report = () => {
              onReport();
              event.stopPropagation();
              close();
            };
            if (loginGate) {
              loginGate().then(() => {
                report();
              });
            } else {
              report();
            }
          }}
        >
          {localize('social_report_1')}
        </OverflowUIListItem>,
      );
    }

    if (editEnabled) {
      overflowItems.push(
        <OverflowUIListItem
          key="edit"
          onClick={({ event, close }) => {
            // Opens popover
            if (typeof onEdit === 'function') {
              onEdit();
            }
            event.stopPropagation();
            // Closes overflow
            close();
          }}
        >
          <span>
            {localize('trips_edit_CTA')} {/* Edit */}
          </span>
        </OverflowUIListItem>,
      );
    }

    if (deleteEnabled && onDelete) {
      overflowItems.push(
        <OverflowUIListItem
          key="delete"
          onClick={({ event, close }) => {
            logInteraction(event, 'delete');
            // Opens popover
            if (typeof onDelete === 'function') {
              onDelete();
            }
            event.stopPropagation();
            // Closes overflow
            close();
          }}
        >
          <span data-test-target="delete-item">
            {localize('inbox_swipe_delete')} {/* Delete */}
          </span>
        </OverflowUIListItem>,
      );
    }

    if (followEnabled && sectionActor) {
      overflowItems.push(
        <OverflowUIListItem
          key="follow"
          onClick={({ event, close }) => {
            this.props.gatedAction();
            event.stopPropagation();
            // Closes overflow
            close();
          }}
        >
          <FollowText profile={sectionActor} />
        </OverflowUIListItem>,
      );
    }

    if (untagEnabled && onUntag) {
      overflowItems.push(
        <OverflowUIListItem
          key="untag"
          onClick={({ event, close }) => {
            logInteraction(event, 'untag');

            // Opens popover
            if (typeof onUntag === 'function') {
              onUntag();
            }
            event.stopPropagation();
            close();
          }}
        >
          <span>
            {localize('social_untag_me')} {/* Untag me */}
          </span>
        </OverflowUIListItem>,
      );
    }

    return (
      <React.Fragment>
        {reportEnabled ? this.renderReportModal(mergedSectionObjectForType.REPORT) : null}
        <OverflowUI
          closeOnScroll={closeOverflowOnScroll}
          onClick={e => logInteraction(e, 'overflowMenuClick')}
        >
          <OverflowUIList>{overflowItems}</OverflowUIList>
        </OverflowUI>
      </React.Fragment>
    );
  };
  render() {
    const {
      deleteObject,
      onEdit,
      untagObject,
      sectionActor,
      sectionObject,
      sectionObjectForType,
      overrideItems = {},
    } = this.props;
    if (!sectionObject && !sectionObjectForType) {
      // One of either sectionObject or sectionObjectForType must be provided
      return null;
    }
    const homogenousSectionObjectForType = sectionObject
      ? fromEntries(
          ['REPORT', 'DELETE', 'EDIT', 'FOLLOW', 'UNTAG'].map(type => [type, sectionObject]),
        )
      : {};
    const mergedSectionObjectForType = {
      ...homogenousSectionObjectForType,
      ...sectionObjectForType,
    };
    const isMe = sectionActor && sectionActor.isMe;
    // Report is enabled if
    // 1) not gated by any features
    // 2) The underlying content type is supported
    // 3) You are not viewing your own content OR this was explicitly overridden
    const reportEnabled =
      featureIsEnabled('social_report_iap_enabled') &&
      (featureIsEnabled('social_report_repost_iap_enabled') ||
        mergedSectionObjectForType.REPORT.__typename !== 'Repost') &&
      ((mergedSectionObjectForType.REPORT.__typename === 'Repost' &&
        SUPPORTED_REPORT_TYPES.includes(
          mergedSectionObjectForType.REPORT.repostedObject?.__typename,
        )) ||
        SUPPORTED_REPORT_TYPES.includes(mergedSectionObjectForType.REPORT.__typename)) &&
      overrideItems.REPORT !== false &&
      (overrideItems.REPORT || !isMe);

    const editEnabled =
      featureIsEnabled('social_edit_enabled') &&
      SUPPORTED_EDIT_TYPES.includes(mergedSectionObjectForType.EDIT.__typename) &&
      onEdit &&
      !(overrideItems.EDIT === false) &&
      (overrideItems.EDIT === true || isMe);

    const deleteEnabled =
      featureIsEnabled('social_delete_enabled') &&
      deleteObject &&
      mergedSectionObjectForType.DELETE &&
      overrideItems.DELETE !== false &&
      (overrideItems.DELETE || isMe);

    const followEnabled =
      featureIsEnabled('social_follow_enabled') &&
      overrideItems.FOLLOW !== false &&
      sectionActor &&
      (overrideItems.FOLLOW || !isMe);

    const untagEnabled =
      featureIsEnabled('social_at_referencing_profile_links') &&
      untagObject &&
      mergedSectionObjectForType.UNTAG &&
      overrideItems.UNTAG !== false;

    const hasItems = reportEnabled || editEnabled || deleteEnabled || followEnabled || untagEnabled;

    if (!hasItems) {
      return null;
    }

    if (deleteEnabled) {
      return (
        <DeleteConfirmation
          objectType={mergedSectionObjectForType.DELETE.__typename}
          deleteObject={deleteObject}
          shiftY={-5}
          below
          alignEdge="right"
        >
          {(target, openDeleteConfirmation) => (
            <React.Fragment>
              <UntagConfirmation untagObject={untagObject} shiftY={-5} below alignEdge="right">
                {(tar, openUntagConfirmation) => (
                  <React.Fragment>
                    {this.renderList({
                      reportEnabled,
                      editEnabled,
                      deleteEnabled,
                      untagEnabled,
                      followEnabled,
                      mergedSectionObjectForType,
                      onDelete: openDeleteConfirmation,
                      onUntag: openUntagConfirmation,
                    })}
                    <span className={styles.float} ref={tar} />
                  </React.Fragment>
                )}
              </UntagConfirmation>
              <span className={styles.float} ref={target} />
            </React.Fragment>
          )}
        </DeleteConfirmation>
      );
    }

    return (
      <UntagConfirmation untagObject={untagObject} shiftY={-5} below alignEdge="right">
        {(tar, openUntagConfirmation) => (
          <React.Fragment>
            {this.renderList({
              reportEnabled,
              editEnabled,
              deleteEnabled,
              untagEnabled,
              followEnabled,
              mergedSectionObjectForType,
              onDelete: null,
              onUntag: openUntagConfirmation,
            })}
            <span className={styles.float} ref={tar} />
          </React.Fragment>
        )}
      </UntagConfirmation>
    );
  }
}

// We need this wrapping component as a class to memorize the modal name.
// The reason is we need a random id because the same picture can appear multiple times on the page (e.g. the photo normally, the photo in a modal etc), thus it needs to be able to
// refer to its own specific modal.
const WrappedSectionOverflowMenu = ({
  sectionActor,
  deleteObject,
  onEdit,
  untagObject,
  closeOverflowOnScroll,
  sectionObject,
  sectionObjectForType,
  overrideItems,
}: ExternalProps) => {
  // Associate this component with a unique ID
  const { current: modalName } = React.useRef(`ReportIAP:${Math.random()}`);
  return (
    <HideOnReadonly>
      <SocialLoginGate pid={40474}>
        {loginGate => (
          <Opener name={modalName}>
            {open => (
              <InteractionLogger>
                {logInteraction => (
                  <FollowAction profile={sectionActor}>
                    {gatedAction => (
                      <SectionOverflowMenu
                        sectionActor={sectionActor}
                        reportModalName={modalName}
                        onReport={open}
                        logInteraction={logInteraction}
                        loginGate={loginGate}
                        deleteObject={deleteObject}
                        sectionObjectForType={sectionObjectForType}
                        onEdit={onEdit}
                        untagObject={untagObject}
                        sectionObject={sectionObject}
                        overrideItems={overrideItems}
                        gatedAction={gatedAction}
                        closeOverflowOnScroll={closeOverflowOnScroll}
                      />
                    )}
                  </FollowAction>
                )}
              </InteractionLogger>
            )}
          </Opener>
        )}
      </SocialLoginGate>
    </HideOnReadonly>
  );
};

/** The overflow menu with follow/unfollow props injected from HoC */
export default WrappedSectionOverflowMenu;
